package app

import kotlinx.browser.document
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import kotlin.math.PI

const val CanvasWidth = 1200.0
const val CanvasHeight = 900.0

class BlurCanvas(private val canvas: HTMLCanvasElement) {
    private val context = canvas.getContext("2d").unsafeCast<CanvasRenderingContext2D>()

    fun configure() {
        canvas.width = CanvasWidth.toInt()
        canvas.height = CanvasHeight.toInt()
    }

    fun draw(
        image: HTMLImageElement?,
        areas: List<BlurArea>,
        currentArea: BlurArea?,
        currentPath: List<Point>,
        tool: Tool,
        showOverlay: Boolean,
        view: ViewState
    ) {
        context.clearRect(0.0, 0.0, CanvasWidth, CanvasHeight)
        if (image == null) return

        val fit = fitImage(image.naturalWidth.toDouble(), image.naturalHeight.toDouble(), CanvasWidth, CanvasHeight)
        context.save()
        context.translate(view.offsetX, view.offsetY)
        context.scale(view.zoom, view.zoom)
        context.drawImage(image, fit.offsetX, fit.offsetY, fit.width, fit.height)

        areas.forEach { drawBlurArea(image, fit, it) }
        if (showOverlay) areas.forEach { strokeArea(fit, it, view.zoom) }
        if (currentArea != null) previewArea(fit, currentArea, view.zoom)
        if (tool == Tool.Freehand && currentPath.isNotEmpty()) previewPath(fit, currentPath, view.zoom)

        context.restore()
    }

    private fun drawBlurArea(image: HTMLImageElement, fit: ImageFit, area: BlurArea) {
        context.save()
        context.filter = "blur(${area.blurRadius}px)"
        clipArea(fit, area)
        context.drawImage(
            image,
            area.x / fit.scale,
            area.y / fit.scale,
            area.width / fit.scale,
            area.height / fit.scale,
            fit.offsetX + area.x,
            fit.offsetY + area.y,
            area.width,
            area.height
        )
        context.restore()
    }

    private fun clipArea(fit: ImageFit, area: BlurArea) {
        when (area.shape) {
            BlurShape.Rectangle -> Unit
            BlurShape.Circle -> {
                context.beginPath()
                context.arc(
                    fit.offsetX + area.x + area.width / 2.0,
                    fit.offsetY + area.y + area.height / 2.0,
                    minOf(area.width, area.height) / 2.0,
                    0.0,
                    2.0 * PI
                )
                context.clip()
            }
            BlurShape.Freehand -> {
                context.beginPath()
                area.path.forEachIndexed { index, point ->
                    if (index == 0) context.moveTo(fit.offsetX + point.x, fit.offsetY + point.y)
                    else context.lineTo(fit.offsetX + point.x, fit.offsetY + point.y)
                }
                context.closePath()
                context.clip()
            }
        }
    }

    private fun strokeArea(fit: ImageFit, area: BlurArea, zoom: Double) {
        context.save()
        context.strokeStyle = "#4f46e5"
        context.lineWidth = 2.0 / zoom
        context.setLineDash(arrayOf(4.0 / zoom, 4.0 / zoom))
        when (area.shape) {
            BlurShape.Rectangle -> context.strokeRect(fit.offsetX + area.x, fit.offsetY + area.y, area.width, area.height)
            BlurShape.Circle -> {
                context.beginPath()
                context.arc(
                    fit.offsetX + area.x + area.width / 2.0,
                    fit.offsetY + area.y + area.height / 2.0,
                    minOf(area.width, area.height) / 2.0,
                    0.0,
                    2.0 * PI
                )
                context.stroke()
            }
            BlurShape.Freehand -> previewPath(fit, area.path, zoom)
        }
        context.restore()
    }

    private fun previewArea(fit: ImageFit, area: BlurArea, zoom: Double) {
        context.save()
        context.fillStyle = "rgba(79, 70, 229, 0.18)"
        strokeArea(fit, area, zoom)
        if (area.shape == BlurShape.Rectangle) {
            context.fillRect(fit.offsetX + area.x, fit.offsetY + area.y, area.width, area.height)
        }
        context.restore()
    }

    private fun previewPath(fit: ImageFit, path: List<Point>, zoom: Double) {
        context.save()
        context.strokeStyle = "#4f46e5"
        context.lineWidth = 3.0 / zoom
        context.setLineDash(arrayOf(5.0 / zoom, 5.0 / zoom))
        context.beginPath()
        path.forEachIndexed { index, point ->
            if (index == 0) context.moveTo(fit.offsetX + point.x, fit.offsetY + point.y)
            else context.lineTo(fit.offsetX + point.x, fit.offsetY + point.y)
        }
        context.stroke()
        context.restore()
    }
}

fun exportBlurredImage(image: HTMLImageElement, areas: List<BlurArea>, onDone: (Blob?) -> Unit) {
    val output = document.createElement("canvas") as HTMLCanvasElement
    output.width = image.naturalWidth
    output.height = image.naturalHeight
    val ctx = output.getContext("2d").unsafeCast<CanvasRenderingContext2D>()
    val fit = fitImage(image.naturalWidth.toDouble(), image.naturalHeight.toDouble(), CanvasWidth, CanvasHeight)

    ctx.drawImage(image, 0.0, 0.0)
    areas.forEach { area ->
        val x = area.x / fit.scale
        val y = area.y / fit.scale
        val width = area.width / fit.scale
        val height = area.height / fit.scale
        val tmp = document.createElement("canvas") as HTMLCanvasElement
        tmp.width = width.toInt().coerceAtLeast(1)
        tmp.height = height.toInt().coerceAtLeast(1)
        val tmpCtx = tmp.getContext("2d").unsafeCast<CanvasRenderingContext2D>()
        tmpCtx.drawImage(image, x, y, width, height, 0.0, 0.0, width, height)
        tmpCtx.filter = "blur(${area.blurRadius}px)"
        tmpCtx.drawImage(tmp, 0.0, 0.0)

        ctx.save()
        when (area.shape) {
            BlurShape.Rectangle -> Unit
            BlurShape.Circle -> {
                ctx.beginPath()
                ctx.arc(x + width / 2.0, y + height / 2.0, minOf(width, height) / 2.0, 0.0, 2.0 * PI)
                ctx.clip()
            }
            BlurShape.Freehand -> {
                ctx.beginPath()
                area.path.forEachIndexed { index, point ->
                    if (index == 0) ctx.moveTo(point.x / fit.scale, point.y / fit.scale)
                    else ctx.lineTo(point.x / fit.scale, point.y / fit.scale)
                }
                ctx.closePath()
                ctx.clip()
            }
        }
        ctx.drawImage(tmp, x, y)
        ctx.restore()
    }

    output.toBlob({ blob: Blob? ->
        onDone(blob)
    }, "image/png")
}

fun downloadPng(blob: Blob, fileName: String) {
    val url = URL.createObjectURL(blob)
    val anchor = document.createElement("a") as HTMLAnchorElement
    anchor.href = url
    anchor.download = fileName
    document.body?.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
}
